import { useMemo } from 'react';

const hexToRgb = hex => {
  let sanitized = hex?.replace('#', '');
  if (sanitized?.length === 3) {
    sanitized = sanitized
      .split('')
      .map(c => c + c)
      .join('');
  }

  const bigint = parseInt(sanitized, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const luminance = ([r, g, b]) => {
  const channel = c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  const R = channel(r);
  const G = channel(g);
  const B = channel(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

const getContrastRatio = (fg, bg) => {
  const L1 = luminance(hexToRgb(fg));
  const L2 = luminance(hexToRgb(bg));
  const [light, dark] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (light + 0.05) / (dark + 0.05);
};

const getCompliance = ratio => ({
  AA: ratio >= 4.5,
  AAA: ratio >= 7,
  AALarge: ratio >= 3,
  AAALarge: ratio >= 4.5,
  AAUI: ratio >= 3,
  AAAUI: ratio >= 4.5,
});

export const contrastMap = [
  { threshold: 12, rating: 5, text: 'Super' },
  { threshold: 7, rating: 4, text: 'Very good' },
  { threshold: 4.5, rating: 3, text: 'Good' },
  { threshold: 3, rating: 2, text: 'Poor' },
  { threshold: 0, rating: 1, text: 'Very poor' },
];

export const useColorContrast = (foreground, background) => {
  return useMemo(() => {
    const ratio = getContrastRatio(foreground, background);
    const compliance = getCompliance(ratio);
    const contrastData = contrastMap.find(level => ratio >= level.threshold);

    return {
      ratio: Number(ratio.toFixed(2)),
      compliance,
      contrastData,
    };
  }, [foreground, background]);
};

export const useGetLabelColor = (backgroundColor, backgroundColorFaded, textColor) => {
  const { ratio: backgroundRatio } = useColorContrast(backgroundColor, backgroundColorFaded);
  if (backgroundRatio >= 4.5) return backgroundColor;

  const { ratio: textRatio } = useColorContrast(textColor, backgroundColorFaded);
  if (textRatio >= 4.5) return textColor;

  const { ratio: blackRatio } = useColorContrast('#000000', backgroundColorFaded);
  const { ratio: whiteRatio } = useColorContrast('#FFFFFF', backgroundColorFaded);

  return blackRatio > whiteRatio ? '#000000' : '#FFFFFF';
};
